// API helpers for the account-less partner pay surface (/api/pay/*),
// authorized by a quote-scoped pay token (ADR-009 §5) carried in the
// `pt` URL param, NOT a JWT. Every call attaches the token as the
// `x-quote-pay-token` header.
//
// We pass `validateStatus: () => true` so a 401/404 (bad/expired link)
// does NOT trip the shared client's 401→refresh→redirect-to-login
// interceptor — a partner has no session to refresh, and bouncing them
// to /login would be wrong. The caller inspects ok/error itself.

import { api } from './client.js';
import type { CheckoutResponse } from './submission.js';

const PT_HEADER = 'x-quote-pay-token';

function withToken(payToken: string) {
  return {
    headers: { [PT_HEADER]: payToken },
    validateStatus: () => true,
  };
}

export type PayQuoteDto = {
  id: string;
  project_title: string | null;
  status: string;
  signature_status: string | null;
  total_sale_amount: number | null;
  currency: string | null;
  requested_finish_date: string | null;
  terms: string | null;
  conditions_text: string | null;
  tax_disclosure: string | null;
  sales_rep_name: string | null;
  sales_rep_email: string | null;
  has_signed_document: boolean;
  can_pay_by_card: boolean;
};

type ApiError = { code: string; message: string };

export type PayQuoteResult =
  | { ok: true; quote: PayQuoteDto }
  | { ok: false; error: ApiError };

export async function getPayQuote(payToken: string): Promise<PayQuoteResult> {
  const res = await api.get<{ ok: true; quote: PayQuoteDto } | { error: ApiError }>(
    '/pay/quote',
    withToken(payToken),
  );
  if (res.status === 200 && 'ok' in res.data) return { ok: true, quote: res.data.quote };
  const error = 'error' in res.data ? res.data.error : { code: 'UNKNOWN', message: 'Could not load this quote.' };
  return { ok: false, error };
}

export async function startPayCheckout(payToken: string): Promise<CheckoutResponse> {
  const res = await api.post<{ ok: true; checkout: CheckoutResponse }>(
    '/pay/checkout',
    {},
    { headers: { [PT_HEADER]: payToken } },
  );
  return res.data.checkout;
}

export async function getSignedDocumentUrl(payToken: string): Promise<string | null> {
  const res = await api.get<{ ok: true; download_url: string } | { error: ApiError }>(
    '/pay/signed-document',
    withToken(payToken),
  );
  if (res.status === 200 && 'ok' in res.data) return res.data.download_url;
  return null;
}

// Mock-provider success simulation for the partner flow (dev only).
// Hits the pay-token-authorized endpoint rather than the JWT-gated
// /dev/mock-payment-success the authenticated checkout uses.
export async function simulatePayMockSuccess(
  payToken: string,
  checkout: CheckoutResponse,
): Promise<void> {
  await api.post(
    '/pay/mock-payment-success',
    {
      provider_transaction_id: checkout.client_action_token,
      amount_cents: checkout.amount_cents,
    },
    { headers: { [PT_HEADER]: payToken } },
  );
}
